


























import Vue from "vue";

const CHECKMARK_SVG =
  '<svg fill="#ffffff" height="48" viewBox="0 0 24 24" width="48"><path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/></svg>';

/**
 * Animated button loading indicator.
 *
 * The button keeps its label in `.btn-text`. Calling the methods below inserts
 * `.loading-indicator-container` > `.loading-indicator-on` + `.loading-indicator-done`
 * and drives the same saving / saving-on / saving-done / saving-invalid sequence
 * as vello.service.animate.btnLoadingIndicator* in vello-app.
 */
export default Vue.extend({
  name: "BtnLoadingIndicator",
  props: {
    label: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      isSaving: false,
      isSavingOn: false,
      isSavingDone: false,
      isSavingInvalid: false,
      showIndicator: false,
      frozenWidth: "" as string,
      indicatorTypes: [] as string[],
      successMarkup: CHECKMARK_SVG,
      successIsText: false,
      loadingGeneration: 0,
      timers: [] as number[]
    };
  },
  computed: {
    phaseClass(): { [key: string]: boolean } {
      return {
        saving: this.isSaving,
        "saving-on": this.isSavingOn,
        "saving-done": this.isSavingDone,
        "saving-invalid": this.isSavingInvalid
      };
    },
    buttonStyle(): { width?: string } {
      if (!this.frozenWidth) return {};
      return { width: this.frozenWidth };
    }
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    btnLoadingIndicatorOn(
      type: string | false = false,
      style = "",
      successContent: string | string[] | false = false
    ): Promise<void> {
      // `style` matches the vello-app signature, where it has no visual effect.
      void style;
      const generation = ++this.loadingGeneration;
      const widthPx = (this.$el as HTMLElement).getBoundingClientRect().width;
      this.frozenWidth = `${widthPx / 16}rem`;
      this.indicatorTypes = type ? [`loading-indicator-${type}`] : [];
      this.applySuccessContent(successContent);
      this.isSavingInvalid = false;
      this.isSavingDone = false;
      this.isSavingOn = false;
      this.isSaving = true;
      this.showIndicator = true;
      this.queueTimer(() => {
        if (generation !== this.loadingGeneration) return;
        this.isSavingOn = true;
      }, 100);
      return new Promise(resolve => {
        this.queueTimer(() => resolve(), 500);
      });
    },
    btnLoadingIndicatorSuccess(
      cb?: (() => void) | false,
      delay?: number,
      type?: string | false
    ): Promise<boolean> {
      this.loadingGeneration += 1;
      const hold = delay || 750;
      if (type) {
        const next = `loading-indicator-${type}`;
        if (this.indicatorTypes.indexOf(next) === -1) {
          this.indicatorTypes = this.indicatorTypes.concat(next);
        }
      }
      this.isSaving = true;
      this.isSavingOn = false;
      this.isSavingDone = true;
      return new Promise(resolve => {
        this.queueTimer(() => {
          if (cb) cb();
          resolve(true);
        }, hold);
      });
    },
    btnLoadingIndicatorOff(
      cb?: (() => void) | false,
      delay = 500
    ): Promise<boolean> {
      this.loadingGeneration += 1;
      this.isSaving = false;
      this.isSavingOn = false;
      this.isSavingDone = false;
      this.isSavingInvalid = false;
      return new Promise(resolve => {
        this.queueTimer(() => {
          this.showIndicator = false;
          this.frozenWidth = "";
          this.indicatorTypes = [];
          if (cb) cb();
          resolve(true);
        }, delay);
      });
    },
    btnLoadingIndicatorInvalid(cb?: () => void): Promise<void> {
      const generation = this.loadingGeneration;
      return new Promise(resolve => {
        this.queueTimer(() => {
          if (generation !== this.loadingGeneration) {
            resolve();
            return;
          }
          this.isSaving = false;
          this.isSavingOn = false;
          this.isSavingDone = false;
          this.isSavingInvalid = true;
          this.queueTimer(() => {
            if (generation !== this.loadingGeneration) {
              resolve();
              return;
            }
            this.isSavingInvalid = false;
            this.showIndicator = false;
            this.frozenWidth = "";
            this.indicatorTypes = [];
            if (cb) cb();
            resolve();
          }, 500);
        }, 500);
      });
    },
    applySuccessContent(successContent: string | string[] | false): void {
      let content = successContent || false;
      if (!content) {
        this.successIsText = false;
        this.successMarkup = CHECKMARK_SVG;
        return;
      }
      if (Array.isArray(content)) {
        content = content[Math.floor(Math.random() * content.length)];
      }
      this.successIsText = true;
      this.successMarkup = content;
    },
    queueTimer(fn: () => void, delay: number): void {
      const id = window.setTimeout(() => {
        this.timers = this.timers.filter(timer => timer !== id);
        fn();
      }, delay);
      this.timers.push(id);
    },
    clearTimers(): void {
      this.timers.forEach(id => window.clearTimeout(id));
      this.timers = [];
    }
  }
});
